
/**
 * Utility functions for processing article references in category summaries
 */

export interface ArticleReferenceMapping {
  orderedIds: string[];
  idToNumber: Map<string, number>;
}

/**
 * Extracts article IDs from text and creates a mapping to sequential numbers
 * @param text - The text containing article references like "Article 01JY7N2XNKSZV0F10EPV9GG611"
 * @returns Mapping object with ordered IDs and ID-to-number mapping
 */
export function extractArticleReferences(text: string): ArticleReferenceMapping {
  // Pattern to match article IDs in the format "Article [ID]" or "Article ID"
  // ULIDs are typically 26 characters long and alphanumeric (case-insensitive)
  const articleIdPattern = /Article\s+([A-Za-z0-9]{26})/gi;
  
  const foundIds = new Set<string>();
  const orderedIds: string[] = [];
  const idToNumber = new Map<string, number>();
  
  let match;
  let articleNumber = 1;
  
  // Find all article references in order of appearance
  while ((match = articleIdPattern.exec(text)) !== null) {
    const articleId = match[1];
    
    // If this ID hasn't been seen before, add it to our mapping
    if (!foundIds.has(articleId)) {
      foundIds.add(articleId);
      orderedIds.push(articleId);
      idToNumber.set(articleId, articleNumber);
      articleNumber++;
    }
  }
  
  return {
    orderedIds,
    idToNumber
  };
}

/**
 * Replaces article IDs in text with sequential numbers
 * @param text - The original text with article IDs
 * @param idToNumber - Mapping from article ID to sequential number
 * @returns Text with IDs replaced by numbers
 */
export function replaceArticleIdsWithNumbers(text: string, idToNumber: Map<string, number>): string {
  // Pattern to match article IDs
  const articleIdPattern = /Article\s+([A-Za-z0-9]{26})/gi;
  
  return text.replace(articleIdPattern, (match, articleId) => {
    const articleNumber = idToNumber.get(articleId);
    return articleNumber ? `Article ${articleNumber}` : match;
  });
}

/**
 * Processes text and returns both the transformed text and the article reference mapping
 * @param text - The original text with article IDs
 * @returns Object with processed text and reference mapping
 */
export function processArticleReferences(text: string): {
  processedText: string;
  referenceMapping: ArticleReferenceMapping;
} {
  const referenceMapping = extractArticleReferences(text);
  const processedText = replaceArticleIdsWithNumbers(text, referenceMapping.idToNumber);
  
  return {
    processedText,
    referenceMapping
  };
}
